import { callApi } from "./api";
import {
  ChecklistFieldDef,
  ChecklistSectionDef,
  ChecklistRepeatableGroupDef,
  DropdownOptionFollowUpDef,
  deedLinkedGroups,
  emptyRepeatableRow,
  fieldHasConfiguredFollowUps,
  normalizeDeedIdForSelect,
  repeatableEntriesKey,
  repeatableRowFollowUpStorageKey,
  triggerFollowBranchKeyForValue,
  sectionGateFields,
  PRE_DISBURSEMENT_DYNAMIC_TAG_IDS,
  POST_DISBURSEMENT_DYNAMIC_TAG_IDS,
  sectionFlowMode,
  isChecklistAnswerFilled,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import {
  normalizeSurveyVerificationRow,
  resolveSurveyNaturePropertyKeys,
  resolveVisibleSurveyNatureGroups,
  surveyNatureFieldScopeKey,
} from "./dynamicChecklistSurveyNature";
import {
  SURVEY_HIERARCHY_META,
  applySurveyHierarchyPrefill,
  buildScheduleNaturePrefillSource,
  flattenSurveyHierarchyRows,
  GeneralDetailsDocumentHierarchy,
  mapSurveyTagToSourceRow,
  resolveMeasurementDirectionTagValue,
  resolveSurveyDocumentFdId,
  resolveSurveyDocumentFdWithSchedules,
  resolveSurveyMappedFieldTag,
  isMeasurementDirectionTag,
  surveyHierarchyLinkedGroups,
} from "./dynamicChecklistSurveyHierarchy";
import { applyEcVerificationPrefill, ecBlocksFromScrutiny } from "./dynamicChecklistEcVerification";
import { DynamicChecklistRuntimeValue } from "../components/DynamicChecklistRuntime/DynamicChecklistRuntime";
import {
  mergeLegalHeirsPrefill,
  isLegalHeirsMappedField,
  legalHeirsRowHasValue,
  parseLegalHeirsListFromScrutiny,
} from "./dynamicChecklistLegalHeirs";
import {
  documentScheduleFieldsInGroup,
  documentScheduleFieldRowHasValue,
  isDocumentScheduleMappedField,
  mergeDocumentSchedulePrefill,
  parseDocumentScheduleSetsFromScrutiny,
} from "./dynamicChecklistDocumentSchedule";
import {
  isChecklistTruthyScalar,
  isChecklistFalsyScalar,
  normalizeMappedDropdownValue,
} from "./dynamicChecklistDropdownValue";

export interface DeedMasterRowLite {
  id: number;
  name: string;
}

/** Hidden repeatable-row key used to show scrutiny doc number beside "Entry #". */
export const REPEATABLE_DOC_NO_META_KEY = "__meta_doc_no";
/** Hidden repeatable-row key used to preview the source document. */
export const REPEATABLE_DOC_BASE_ID_META_KEY = "__meta_doc_base_id";
/** Hidden repeatable-row key for scrutiny document id (stable row matching). */
export const REPEATABLE_DS_ID_META_KEY = "__meta_ds_id";
/** Hidden repeatable-row key for List Of Documents save order. */
export const REPEATABLE_POSITION_META_KEY = "__meta_position";
/** Synthetic key suffix for section-level Missing LOD repeated rows. */
export const MISSING_LOD_REPEATABLE_SUFFIX = "__missing_lod_auto__";
export const MISSING_LOD_META_FILE_NO_KEY = "__missing_lod_fileNo";
export const MISSING_LOD_META_FILE_NAME_KEY = "__missing_lod_fileName";
/** Synthetic key suffixes for addon pre/post disbursement repeated rows. */
export const PRE_DISBURSEMENT_REPEATABLE_SUFFIX = "__pre_disbursement_auto__";
export const POST_DISBURSEMENT_REPEATABLE_SUFFIX = "__post_disbursement_auto__";
export const PRE_DISBURSEMENT_META_ID_KEY = "__meta_pre_disburse_id";
export const POST_DISBURSEMENT_META_ID_KEY = "__meta_post_disburse_id";

export const PRE_DISBURSEMENT_TAG_TO_ROW_KEY: Record<string, string> = {
  preDisbursementDate: "date",
  preDisbursementDocumentNumber: "document_number",
  preDisbursementParticularDeed: "particular_deed",
  preDisbursementNatureDeed: "nature_deed",
};

export const POST_DISBURSEMENT_TAG_TO_ROW_KEY: Record<string, string> = {
  postDisbursementDate: "date",
  postDisbursementDocumentNumber: "document_number",
  postDisbursementParticularDeed: "particular_deed",
  postDisbursementNatureDeed: "nature_deed",
};

/**
 * Maps `deed_master.id` / `nature_doc` (from scrutiny) to keys returned by
 * `get_dynamic_verificationchecklist.php` under `data.deed`.
 */
const DEED_MASTER_ID_TO_API_KEY: Record<number, string> = {
  1: "partitionDeed",
  2: "saleDeed",
  7: "giftDeed",
  12: "settlementDeed",
  9: "willDeed",
  22: "leaseDeed",
  11: "attorneyDeed",
  13: "releaseDeed",
  17: "rectificationDeed",
  18: "consentDeed",
  42: "gazetteofDoc",
  26: "deathCertificate",
  27: "legalHeirsCertificate",
  160: "legalHeirsGenealogy",
};

/** API roots outside `data.deed` keyed by deed_master / nature_doc id. */
const SCRUTINY_SECTION_BY_DEED_MASTER_ID: Record<
  number,
  { root: string; key: string; filterByNatureDoc?: boolean }
> = {
  8: { root: "propertytax", key: "propertytax" },
  27: { root: "legalHeirsVerify", key: "legalHeirs", filterByNatureDoc: true },
  160: { root: "legalHeirsVerify", key: "legalHeirs", filterByNatureDoc: true },
  80: { root: "EBreceiptData", key: "EBreceipt" },
  137: { root: "HsdpattasVerify", key: "Hsdpattas" },
};

/** Template ids used by legacy `get_verification.php` (same deed families). */
const TEMPLATE_ID_TO_API_KEY: Record<number, string> = {
  1: "partitionDeed",
  7: "giftDeed",
  12: "settlementDeed",
  9: "willDeed",
  11: "attorneyDeed",
};

/** Fallback when deed_master id does not match nature_doc id. */
const DEED_NAME_TO_API_KEY: { pattern: string; key: string }[] = [
  { pattern: "gift settlement", key: "giftDeed" },
  { pattern: "settlement", key: "settlementDeed" },
  { pattern: "partition", key: "partitionDeed" },
  { pattern: "sale deed", key: "saleDeed" },
  { pattern: "sale", key: "saleDeed" },
  { pattern: "will", key: "willDeed" },
  { pattern: "lease", key: "leaseDeed" },
  { pattern: "attorney", key: "attorneyDeed" },
  { pattern: "release", key: "releaseDeed" },
  { pattern: "rectification", key: "rectificationDeed" },
  { pattern: "consent", key: "consentDeed" },
  { pattern: "gazette", key: "gazetteofDoc" },
  { pattern: "death certificate", key: "deathCertificate" },
  { pattern: "legal heir", key: "legalHeirsCertificate" },
  { pattern: "genealogy", key: "legalHeirsGenealogy" },
];

function pickRelationship(item: any): string {
  if (!item || typeof item !== "object") return "";
  const v =
    item.relationship ??
    item.relationshipMentioned ??
    item.willdeadreationship ??
    item.relationShipmentioned ??
    item.partionRelationship ??
    "";
  return v == null ? "" : String(v).trim();
}

function pickExecutantDerived(item: any): string {
  if (!item || typeof item !== "object") return "";
  const v =
    item.executant_derived_by ??
    item.executantDerivedBy ??
    item.derivedBy ??
    item.surveyNo ??
    "";
  return v == null ? "" : String(v).trim();
}

/** Extract each General Detail address line from API / legacy shapes. */
export function extractAddressOfPropertyLines(raw: unknown): string[] {
  if (raw == null) return [];
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith("{")) {
      const parsed = parseAddressOfPropertyStored(t);
      if (parsed.length && (parsed.length > 1 || parsed[0] !== "")) {
        return parsed.filter((line) => line !== "");
      }
    }
    if (t.includes("\n")) {
      return t
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== "");
    }
    return [t];
  }
  if (Array.isArray(raw)) {
    return raw
      .map((v) => (v == null ? "" : String(v).trim()))
      .filter((v) => v !== "");
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.value === "string" && obj.value.trim()) {
      return [obj.value.trim()];
    }
    if (Array.isArray(obj.value)) {
      return extractAddressOfPropertyLines(obj.value);
    }
    const lineKeys = Object.keys(obj)
      .filter((key) => key !== "verified" && key !== "value" && /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b));
    if (lineKeys.length) {
      return lineKeys
        .map((key) => (obj[key] == null ? "" : String(obj[key]).trim()))
        .filter((line) => line !== "");
    }
  }
  const asString = String(raw).trim();
  return asString ? [asString] : [];
}

/** Parse value stored in dynamic checklist `checklistValues[fieldId]`. */
export function parseAddressOfPropertyStored(value: string | undefined): string[] {
  const s = value == null ? "" : String(value).trim();
  if (!s) return [""];
  if (s.startsWith("{")) {
    try {
      const obj = JSON.parse(s) as Record<string, unknown>;
      const lineKeys = Object.keys(obj)
        .filter((key) => key !== "verified" && key !== "value" && /^\d+$/.test(key))
        .sort((a, b) => Number(a) - Number(b));
      if (lineKeys.length) {
        return lineKeys.map((key) => (obj[key] == null ? "" : String(obj[key]).trim()));
      }
    } catch {
      /* fall through */
    }
  }
  if (s.includes("\n")) {
    const lines = s
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    return lines.length ? lines : [""];
  }
  return [s];
}

/** Serialize multiple address lines for storage in `checklistValues`. */
export function serializeAddressOfPropertyStored(lines: string[]): string {
  const trimmed = lines.map((line) => (line == null ? "" : String(line).trim()));
  const nonEmpty = trimmed.filter((line) => line !== "");
  if (!nonEmpty.length) return "";
  if (nonEmpty.length === 1 && trimmed.length === 1) {
    return nonEmpty[0];
  }
  const obj: Record<string, string> = {};
  trimmed.forEach((line, index) => {
    obj[String(index + 1)] = line;
  });
  return JSON.stringify(obj);
}

/** Flatten address lines for export / single-line display. */
export function formatAddressOfPropertyDisplayValue(raw: string | undefined): string {
  const lines = parseAddressOfPropertyStored(raw).filter((line) => line.trim() !== "");
  if (!lines.length) return "—";
  return lines.join("\n");
}

export function isAddressOfPropertyMappedField(field: {
  scrutinyLinkedDeedId?: string | number;
}): boolean {
  const tag = normalizeDeedIdForSelect(field.scrutinyLinkedDeedId).toLowerCase();
  return tag === "addressofproperty";
}

/** Links each address checklist line to a scrutiny document General Detail row. */
export interface AddressPropertyLineMeta {
  lineIndex?: number;
  fodFk: number;
  generalDetailId?: number;
  fdId?: number;
  dsId?: number;
  address?: string;
}

export function parseAddressPropertyLineMeta(scrutinyApiData: any): AddressPropertyLineMeta[] {
  const pd = scrutinyApiData?.propertyDetails;
  const raw = pd?.addressofPropertyLines ?? pd?.addressOfPropertyLines;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row: any, index: number) => ({
      lineIndex: row?.lineIndex != null ? Number(row.lineIndex) : index,
      fodFk: Number(row?.fodFk ?? row?.fod_fk ?? 0),
      generalDetailId: Number(row?.generalDetailId ?? row?.general_detail_id ?? 0) || undefined,
      fdId: Number(row?.fdId ?? row?.fd_id ?? 0) || undefined,
      dsId: Number(row?.dsId ?? row?.ds_id ?? 0) || undefined,
      address: row?.address != null ? String(row.address) : undefined,
    }))
    .filter((row: AddressPropertyLineMeta) => Number.isFinite(row.fodFk) && row.fodFk > 0);
}

export function buildAddressPropertyDetailUpdates(
  storedValue: string | undefined,
  metaLines: AddressPropertyLineMeta[]
): { addressLines: { fodFk: number; generalDetailId?: number; address: string }[] } {
  const lines = parseAddressOfPropertyStored(storedValue ?? "");
  const count = Math.max(lines.length, metaLines.length);
  const addressLines: { fodFk: number; generalDetailId?: number; address: string }[] = [];
  for (let i = 0; i < count; i++) {
    const meta = metaLines[i];
    if (!meta || !meta.fodFk) continue;
    addressLines.push({
      fodFk: meta.fodFk,
      generalDetailId: meta.generalDetailId,
      address: lines[i] == null ? "" : String(lines[i]).trim(),
    });
  }
  return { addressLines };
}

function pickAddressFromScrutinyItem(item: any): string {
  if (!item || typeof item !== "object") return "";
  const raw =
    item.addressofProperty ??
    item.addressOfProperty ??
    item.address_of_property ??
    item.address;
  const lines = extractAddressOfPropertyLines(raw);
  return serializeAddressOfPropertyStored(lines);
}

/** Numeric zero means “no missing sheets” — show/save as empty, not "0". */
export function normalizeMissingSheetsScalar(
  value: string | number | undefined | null
): string {
  const text = value == null ? "" : String(value).trim();
  if (!text) return "";
  if (/^0+(\.0+)?$/.test(text)) return "";
  return text;
}

export function isMissingSheetsField(field: {
  scrutinyLinkedDeedId?: string | number;
  label?: string;
  type?: string;
}): boolean {
  const tag = normalizeDynamicTagId(
    resolveScrutinyFieldTag(field as ChecklistFieldDef)
  ).toLowerCase();
  if (tag === "missingsheets") return true;
  const label = String(field.label ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");
  return label.includes("missing") && (label.includes("sheet") || label.includes("page"));
}

function reconcileMissingSheetsWhenCountsMatch(row: Record<string, string>): void {
  const totalRaw = String(row.totalSheets ?? "").trim();
  const availRaw = String(
    row.availableSheets ?? row.sheet_count ?? row.sheetCount ?? ""
  ).trim();
  if (!totalRaw || !availRaw) return;
  const total = Number(totalRaw);
  const avail = Number(availRaw);
  if (!Number.isFinite(total) || !Number.isFinite(avail) || total !== avail) return;
  row.missingSheets = normalizeMissingSheetsScalar(row.missingSheets);
}

/** Clear missing-sheet fields when total and available counts match (per row). */
export function reconcileMissingSheetsInFieldRow(
  fields: ChecklistFieldDef[],
  row: Record<string, string>
): void {
  let total = "";
  let avail = "";
  const missingFields: ChecklistFieldDef[] = [];
  fields.forEach((f) => {
    const tag = normalizeDynamicTagId(resolveScrutinyFieldTag(f)).toLowerCase();
    const cell = String(row[f.id] ?? "").trim();
    if (tag === "totalsheets" && cell) total = cell;
    if ((tag === "availablesheets" || tag === "sheet_count" || tag === "sheetcount") && cell) {
      avail = cell;
    }
    if (isMissingSheetsField(f)) missingFields.push(f);
  });
  if (!total || !avail || total !== avail) return;
  missingFields.forEach((f) => {
    row[f.id] = "";
  });
  reconcileMissingSheetsWhenCountsMatch(row);
}

/** Normalize one scrutiny deed row into tag-friendly keys. */
function normalizeScrutinyDeedRow(item: any): Record<string, string> {
  const relationship = pickRelationship(item);
  const executant = pickExecutantDerived(item);
  const out: Record<string, string> = {
    executant,
    relationship,
    derivedBy: executant,
    relationshipMentioned: relationship,
    executant_derived_by: executant,
    surveyNo: executant,
  };
  if (item && typeof item === "object") {
    if (Array.isArray((item as any).legal_heirs_list)) {
      const list = (item as any).legal_heirs_list as unknown[];
      const names = list
        .map((x) => String(x ?? "").trim())
        .filter((x) => x !== "");
      if (names.length) {
        out.legal_heirs_list_json = JSON.stringify(names);
      }
    }
    if (Array.isArray((item as any).schedule_deed)) {
      out.schedule_deed_json = JSON.stringify((item as any).schedule_deed);
    }
    Object.keys(item).forEach((k) => {
      const v = (item as any)[k];
      if (v != null && typeof v !== "object") {
        out[k] = String(v).trim();
      }
    });
    const address = pickAddressFromScrutinyItem(item);
    if (address) {
      out.addressofProperty = address;
      out.addressOfProperty = address;
    }
  }
  if (out.missingSheets != null) {
    out.missingSheets = normalizeMissingSheetsScalar(out.missingSheets);
  }
  reconcileMissingSheetsWhenCountsMatch(out);
  return out;
}

export function apiKeyForDeedMasterId(
  deedMasterId: number | undefined,
  deedMasterList?: DeedMasterRowLite[]
): string | null {
  if (!deedMasterId) return null;
  const id = Number(deedMasterId);
  if (DEED_MASTER_ID_TO_API_KEY[id]) return DEED_MASTER_ID_TO_API_KEY[id];
  if (TEMPLATE_ID_TO_API_KEY[id]) return TEMPLATE_ID_TO_API_KEY[id];
  const deed = (deedMasterList || []).find((d) => Number(d.id) === id);
  if (!deed) return null;
  const norm = deed.name.toLowerCase();
  for (let i = 0; i < DEED_NAME_TO_API_KEY.length; i++) {
    const row = DEED_NAME_TO_API_KEY[i];
    if (norm.indexOf(row.pattern) >= 0) return row.key;
  }
  return null;
}

/** Merge deed arrays from legacy verification API into dynamic scrutiny payload. */
export function mergeScrutinyDeedPayload(
  dynamicPayload: any,
  verificationDeed: any
): any {
  const out = dynamicPayload && typeof dynamicPayload === "object" ? { ...dynamicPayload } : {};
  if (!out.deed || typeof out.deed !== "object") out.deed = {};
  if (!verificationDeed || typeof verificationDeed !== "object") return out;

  Object.keys(verificationDeed).forEach((apiKey) => {
    const src = verificationDeed[apiKey];
    if (!Array.isArray(src)) return;
    const dest: any[] = Array.isArray(out.deed[apiKey]) ? [...out.deed[apiKey]] : [];
    if (!dest.length) {
      out.deed[apiKey] = src;
      return;
    }
    const srcByKey = new Map<string, any>();
    src.forEach((item: any) => {
      const key = scrutinyDeedRowIdentity(item);
      if (key) srcByKey.set(key, item);
    });
    out.deed[apiKey] = dest.map((item: any, idx: number) => {
      const key = scrutinyDeedRowIdentity(item);
      const match = key ? srcByKey.get(key) : src[idx];
      if (match && typeof match === "object") {
        return { ...item, ...match };
      }
      return item;
    });
  });
  return out;
}

/**
 * Loads scrutiny values for a file from dynamic + legacy verification APIs.
 */
export function fetchScrutinyChecklistForFile(
  fileId: string | number,
  userId?: string | number
): Promise<any> {
  const payload = { fileNo: fileId, userId: userId || "" };
  const dynamicPromise = callApi("POST", "get_dynamic_verificationchecklist.php", payload)
    .then((res) => res?.data)
    .then((response) => (response?.data ? response.data : {}))
    .catch(() => ({}));

  const verificationPromise = callApi("POST", "get_verification.php", { fileNo: fileId })
    .then((res) => res?.data)
    .then((response) => (response?.data ? response.data : {}))
    .catch(() => ({}));

  return Promise.all([dynamicPromise, verificationPromise]).then(([dynamic, verification]) =>
    mergeScrutinyDeedPayload(dynamic, verification?.deed)
  );
}

export function fetchDeedMasterList(): Promise<DeedMasterRowLite[]> {
  return callApi("GET", "deed_master_get.php")
    .then((res) => res?.data)
    .then((response) => {
      const raw = response?.data;
      if (!Array.isArray(raw)) return [];
      return raw
        .map((row: any) => ({
          id: Number(row.id),
          name: String(row.name || "").trim(),
        }))
        .filter((row: DeedMasterRowLite) => Number.isFinite(row.id) && row.id > 0 && row.name);
    })
    .catch(() => [] as DeedMasterRowLite[]);
}

export function normalizeDynamicTagId(tagId: string | number | undefined | null): string {
  let s = String(tagId ?? "")
    .trim()
    .replace(/^@+|@+$/g, "");
  const emDash = s.indexOf(" — ");
  if (emDash > 0) s = s.slice(0, emDash).trim();
  const hyphen = s.indexOf(" - ");
  if (hyphen > 0) s = s.slice(0, hyphen).trim();
  return s;
}

/** Map checklist / input-master tag aliases to scrutiny save tags. */
const SCRUTINY_SAVE_TAG_ALIASES: Record<string, string> = {
  favour: "favourOf",
  favourof: "favourOf",
  favour_of: "favourOf",
  executant: "executedBy",
  executantname: "executedBy",
  executedby: "executedBy",
  relationship: "relationshipMentioned",
  relationshipmentioned: "relationshipMentioned",
  partionrelationship: "relationshipMentioned",
  willdeadreationship: "relationshipMentioned",
  relationshiptmentioned: "relationshipMentioned",
  executant_derived_by: "surveyNo",
  executantderivedby: "surveyNo",
  derivedby: "surveyNo",
  surveyno: "surveyNo",
  docno: "deedReference",
  doc_no: "deedReference",
  deedreference: "deedReference",
  dateverified: "date",
  dataverified: "date",
  tobeobtained: "obtined",
  originalcopyavailable: "obtined",
  nowscrutinized: "org_pres",
  oldsurveyno: "old_survey_no",
  newsurveyno: "new_survey_no",
  old_surevey: "old_survey_no",
  current_survey: "new_survey_no",
  registered: "reg",
};

const DEED_FIELD_ID_TAG_RE = /^f_deed\d+_([a-zA-Z0-9_]+)_/;

/** Recover input-master `name` from auto-generated deed field ids. */
export function inferDeedFieldTagFromFieldId(fieldId: string | undefined | null): string {
  const match = String(fieldId ?? "").match(DEED_FIELD_ID_TAG_RE);
  return match ? match[1] : "";
}

export function canonicalScrutinySaveTag(tag: string | undefined | null): string {
  const base = normalizeDynamicTagId(tag);
  if (!base) return "";
  const key = normalizeTagLookupKey(base);
  return SCRUTINY_SAVE_TAG_ALIASES[key] || base;
}

function isScrutinyVerificationLabel(norm: string): boolean {
  return (
    norm.includes("verified") ||
    norm.includes("verfy") ||
    norm.includes("verify") ||
    norm.includes("verification")
  );
}

/** Yes/no payment-status questions — not the document `date` column. */
function isPropertyTaxPaidStatusLabel(norm: string): boolean {
  return (
    (norm.includes("paid") &&
      (norm.includes("up to date") ||
        norm.includes("upto date") ||
        norm.includes("uptodate") ||
        norm.includes("up-to-date"))) ||
    (norm.includes("tax") && norm.includes("paid") && !norm.includes("verified"))
  );
}

/** Infer verification-tag for yes/no scrutiny checks (not data columns like extent). */
function inferScrutinyVerificationTagFromLabel(norm: string): string {
  if (
    (norm.includes("property") && norm.includes("detail")) ||
    (norm.includes("survey") && norm.includes("plot") && norm.includes("extent"))
  ) {
    return "";
  }
  if (norm.includes("extent")) return "deedExtentverfy";
  if (norm.includes("plot")) return "deedplotNoverfy";
  if (norm.includes("door")) return "deedDoorNoverfy";
  if (norm.includes("flat")) return "deedflatnoverfy";
  if (norm.includes("layout")) return "deedlayoutnameverfy";
  if (norm.includes("street")) return "deedstreetnameverfy";
  if (norm.includes("apartment") || norm.includes("appt")) return "deedapartnameverfy";
  if (norm.includes("north")) return "boundverfyNorth";
  if (norm.includes("south")) return "boundverfySouth";
  if (norm.includes("east")) return "boundverfyEast";
  if (norm.includes("west")) return "boundverfyWest";
  if (norm.includes("measurement")) return "measurementextentmatch";
  if (norm.includes("issued")) return "pattaissuedverfy";
  return "";
}

/** Scrutiny `doc_scurty.obtined` / `org_pres` — not yes/no verification questions. */
export function isOriginalPresentScrutinySaveTag(tag: string | undefined | null): boolean {
  const normalized = canonicalScrutinySaveTag(normalizeDynamicTagId(tag));
  return normalized === "obtined" || normalized === "org_pres";
}

export function isOriginalPresentYesNoValue(value: string | undefined | null): boolean {
  const lower = String(value ?? "").trim().toLowerCase();
  return lower === "yes" || lower === "no" || lower === "true" || lower === "false";
}

/** Direct `doc_scurty` data columns — must not receive yes/no checklist answers. */
const SCRUTINY_SCALAR_DATA_COLUMN_KEYS = new Set([
  "deedreference",
  "docno",
  "date",
  "dataverified",
  "dateverified",
  "extent",
  "issuedby",
  "oldsurveyno",
  "oldsurevey",
  "newsurveyno",
  "currentsurvey",
  "totalsheets",
  "availablesheets",
  "missingsheets",
  "sheetcount",
  "reg",
  "registered",
  "remark",
]);

export function isScrutinyScalarDataColumnSaveTag(tag: string | undefined | null): boolean {
  const canonical = canonicalScrutinySaveTag(normalizeDynamicTagId(tag));
  if (!canonical || isOriginalPresentScrutinySaveTag(canonical)) return false;
  return SCRUTINY_SCALAR_DATA_COLUMN_KEYS.has(normalizeTagLookupKey(canonical));
}

function inferDeedFieldTagFromLabelForYesNo(label: string | undefined): string {
  const norm = String(label ?? "").trim().toLowerCase();
  if (!norm) return "";
  if (isScrutinyVerificationLabel(norm) || norm.includes("match") || norm.endsWith("?")) {
    return inferScrutinyVerificationTagFromLabel(norm);
  }
  return "";
}

/** Match scrutiny form "To Be Obtained" labels only — not MOD-list verification questions. */
function isToBeObtainedFieldLabel(norm: string): boolean {
  if (norm.includes("to be obtained") || norm.includes("to be obtain")) return true;
  if (norm.includes("original copy")) return true;
  if (norm.includes("?") && norm.includes("obtained")) return false;
  if (norm.includes("include") && norm.includes("obtained")) return false;
  if (norm.includes("documents") && norm.includes("obtained")) return false;
  if (norm.includes("list") && norm.includes("obtained")) return false;
  return false;
}

/** Match scrutiny form "Now Scrutinized" labels only. */
function isNowScrutinizedFieldLabel(norm: string): boolean {
  if (norm.includes("now scrutinized") || norm.includes("now scrutenized")) return true;
  if (norm.includes("org pres")) return true;
  if (norm.includes("?") && norm.includes("scrutinized")) return false;
  return false;
}

export function inferDeedFieldTagFromLabel(label: string | undefined): string {
  const norm = String(label ?? "").trim().toLowerCase();
  if (!norm) return "";
  if (isScrutinyVerificationLabel(norm)) {
    return inferScrutinyVerificationTagFromLabel(norm);
  }
  if (norm.includes("favour") || norm.includes("claimant")) return "favourOf";
  if (norm.includes("executed") || norm.includes("executant")) return "executedBy";
  if (norm.includes("relationship")) return "relationshipMentioned";
  if (
    (norm.includes("doc") && (norm.includes("no") || norm.includes("number"))) ||
    norm.includes("document no")
  ) {
    return "deedReference";
  }
  if (isPropertyTaxPaidStatusLabel(norm)) return "";
  if (norm.includes("date")) return "date";
  if (norm.includes("registered")) return "reg";
  if (norm.includes("condition")) return "condition_mention";
  if (norm.includes("extent")) return "extent";
  if (norm.includes("remark")) return "remark";
  if (norm.includes("total") && (norm.includes("sheet") || norm.includes("page"))) return "totalSheets";
  if (norm.includes("available") && (norm.includes("sheet") || norm.includes("page"))) {
    return "availableSheets";
  }
  if (norm.includes("missing") && (norm.includes("sheet") || norm.includes("page"))) {
    return "missingSheets";
  }
  if (norm.includes("survey") && norm.includes("no")) return "surveyNo";
  if (isToBeObtainedFieldLabel(norm)) return "obtined";
  if (isNowScrutinizedFieldLabel(norm)) return "org_pres";
  return "";
}

/** Resolve scrutiny read/write tag for a checklist field (prefill + save). */
export function resolveScrutinyFieldTag(field: ChecklistFieldDef): string {
  const mapped = normalizeDynamicTagId(field.scrutinyLinkedDeedId);
  if (mapped) {
    if (field.type === "yes_no" && isScrutinyScalarDataColumnSaveTag(mapped)) {
      return inferDeedFieldTagFromLabelForYesNo(field.label);
    }
    return mapped;
  }
  const fromId = inferDeedFieldTagFromFieldId(field.id);
  if (fromId) {
    if (field.type === "yes_no" && isScrutinyScalarDataColumnSaveTag(fromId)) {
      return inferDeedFieldTagFromLabelForYesNo(field.label);
    }
    return fromId;
  }
  if (field.type === "yes_no") {
    return inferDeedFieldTagFromLabelForYesNo(field.label);
  }
  return inferDeedFieldTagFromLabel(field.label);
}

export function resolveScrutinyFieldSaveTag(field: ChecklistFieldDef): string {
  return canonicalScrutinySaveTag(resolveScrutinyFieldTag(field));
}

function normalizeTagLookupKey(key: string): string {
  return String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function tagKeyVariants(tagId: string): string[] {
  const base = normalizeDynamicTagId(tagId);
  if (!base) return [];
  const variants = new Set<string>([base, base.toLowerCase()]);
  const camel = base.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  variants.add(camel);
  const snake = base.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
  variants.add(snake);
  const aliases: Record<string, string[]> = {
    issuedby: ["issued_by", "issuedBy", "legalHeirsObtained", "receiptIssuedByVerify", "propertyTaxVerify"],
    date: ["date", "dataVerified", "dateVerified"],
    obtined: ["obtined", "toBeObtained", "originalCopyAvailable"],
    org_pres: ["org_pres", "nowScrutinized"],
    tobeobtained: ["obtined", "toBeObtained", "originalCopyAvailable"],
    nowscrutinized: ["org_pres", "nowScrutinized"],
    favourof: ["favourOf", "favour_of", "favour"],
    executedby: ["executedBy", "executantName"],
    deedreference: ["deedReference", "doc_no", "docNo"],
    surveyno: ["surveyNo", "executant_derived_by", "executantDerivedBy", "derivedBy"],
    totalsheets: ["totalSheets"],
    availablesheets: ["availableSheets", "sheet_count", "sheetCount"],
    missingsheets: ["missingSheets"],
    relationship: ["relationship", "relationshipMentioned", "partionRelationship", "willdeadreationship"],
    oldsurveyno: ["old_survey_no", "oldSurveyNo", "old_surevey", "old_survey"],
    newsurveyno: ["new_survey_no", "current_survey", "surveyNo", "pattaExtractSno"],
    legalhiers: ["legal_hiers", "legal_heirs", "legai_heirs"],
    legalheirs: ["legal_hiers", "legal_heirs", "legai_heirs"],
    reg: ["reg", "registered"],
    registered: ["registered", "reg"],
  };
  const aliasKey = normalizeTagLookupKey(base);
  (aliases[aliasKey] || []).forEach((a) => variants.add(a));
  return [...variants];
}

function lookupRowTagValue(tagId: string, row: Record<string, string>): string {
  for (const key of tagKeyVariants(tagId)) {
    const val = row[key];
    if (val != null && String(val).trim() !== "") {
      return String(val).trim();
    }
  }
  const wanted = normalizeTagLookupKey(tagId);
  if (!wanted) return "";
  for (const rawKey of Object.keys(row)) {
    if (normalizeTagLookupKey(rawKey) !== wanted) continue;
    const val = row[rawKey];
    if (val != null && String(val).trim() !== "") {
      return String(val).trim();
    }
  }
  return "";
}

function resolveTagValue(tagId: string, row: Record<string, string>): string {
  const tag = normalizeDynamicTagId(tagId).toLowerCase();
  if (tag === "executant" || tag === "executant_derived_by" || tag === "executantderivedby") {
    return (
      row.executant_derived_by ||
      row.executantDerivedBy ||
      row.surveyNo ||
      row.executant ||
      row.derivedBy ||
      ""
    );
  }
  if (tag === "relationship") return row.relationship || row.relationshipMentioned || "";
  if (tag === "obtined" || tag === "tobeobtained" || tag === "originalcopyavailable") {
    return (
      row.toBeObtained ||
      row.originalCopyAvailable ||
      lookupRowTagValue("obtined", row) ||
      ""
    );
  }
  if (tag === "org_pres" || tag === "nowscrutinized") {
    return row.nowScrutinized || lookupRowTagValue("org_pres", row) || "";
  }
  if (tag === "favourof" || tag === "favour_of") {
    return (
      row.favourOf ||
      lookupRowTagValue("favour", row) ||
      row.favour ||
      ""
    );
  }
  if (tag === "executedby" || tag === "executantname") {
    return row.executedBy || row.executantName || lookupRowTagValue("executedBy", row) || "";
  }
  if (tag === "old_survey_no" || tag === "oldsurveyno") {
    return (
      row.old_survey_no ||
      row.oldSurveyNo ||
      row.old_surevey ||
      row.old_survey ||
      ""
    );
  }
  if (tag === "new_survey_no" || tag === "newsurveyno") {
    return (
      row.new_survey_no ||
      row.current_survey ||
      row.surveyNo ||
      row.pattaExtractSno ||
      ""
    );
  }
  if (tag === "reg" || tag === "registered") {
    return row.reg || row.registered || lookupRowTagValue("reg", row) || "";
  }
  if (isMeasurementDirectionTag(tagId)) {
    return resolveMeasurementDirectionTagValue(tagId, row);
  }
  if (tag === "addressofproperty") {
    const stored =
      row.addressofProperty ||
      row.addressOfProperty ||
      row.address_of_property ||
      row.address ||
      "";
    if (!stored) return "";
    const lines = parseAddressOfPropertyStored(stored);
    return lines.filter((line) => line.trim() !== "").join("\n") || stored;
  }
  if (tag === "appaidverfy" || tag === "appidverfy" || tag === "app_id") return row.AppIdverfy || row.app_id || "";
  if (tag === "borroweraadharname") return row.borrowerAadharName || row.borrower_name || "";
  if (tag === "coborroweraadharname") return row.coBorrowerAadharName || row.co_applicant || "";
  if (tag === "owneraadharname") return row.ownerAadharName || row.owner_name || "";
  if (tag === "natureofproperty") return row.natureofProperty || row.nature_of_property || "";
  if (tag === "theresuchpropertyownername") {
    return row.thereSuchPropertyOwnerName || row.there_such || row.there_sush_pon || "";
  }
  if (tag === "totalextent") return row.totalExtent || row.total_extend || "";
  if (tag === "extentunit") return row.extentUnit || row.extent_unit || row.total_unit || "";
  if (tag === "takeoverbank") return row.takeoverBank || row.takeover_bank || "";
  if (tag === "finalremarks") return row.finalRemarks || row.note || "";
  if (tag === "totalsheets") {
    return row.totalSheets || lookupRowTagValue("totalSheets", row) || "";
  }
  if (tag === "availablesheets") {
    return (
      row.availableSheets ||
      row.sheet_count ||
      row.sheetCount ||
      lookupRowTagValue("availableSheets", row) ||
      ""
    );
  }
  if (tag === "missingsheets") {
    return normalizeMissingSheetsScalar(
      row.missingSheets || lookupRowTagValue("missingSheets", row) || ""
    );
  }
  const normalizedTag = normalizeDynamicTagId(tagId);
  if (
    normalizedTag.startsWith("gdSchedule_") ||
    normalizedTag.startsWith("gdSurvey_") ||
    normalizedTag.startsWith("gdBoundary_") ||
    normalizedTag.startsWith("gdMeasure")
  ) {
    const hierarchyVal = mapSurveyTagToSourceRow(normalizedTag, row);
    if (hierarchyVal) return hierarchyVal;
  }
  const generic = lookupRowTagValue(tagId, row);
  if (generic) return generic;
  return row[tag] || row[tagId] || "";
}

function fieldAllowsScrutinyPrefill(field: ChecklistFieldDef): boolean {
  if (field.dynamicValueSource === "front_office") return false;
  if (!resolveScrutinyFieldTag(field)) return false;
  return (
    field.type === "text" ||
    field.type === "date" ||
    field.type === "remarks" ||
    field.type === "dropdown" ||
    field.type === "yes_no"
  );
}

function normalizeFollowUpPrefillValue(
  fu: DropdownOptionFollowUpDef,
  tag: string,
  raw: string
): string {
  const val = String(raw ?? "").trim();
  if (!val) return "";
  if (fu.questionType === "checkbox") {
    return isChecklistTruthyScalar(val) ? "true" : "false";
  }
  if (fu.questionType === "yes_no") {
    if (isChecklistTruthyScalar(val)) return "Yes";
    if (isChecklistFalsyScalar(val)) return "No";
    return val;
  }
  if (fu.questionType === "dropdown" && fu.followUpOptions?.length) {
    const synthetic: ChecklistFieldDef = {
      id: fu.valueKey,
      label: fu.questionLabel,
      type: "dropdown",
      order: 0,
      options: [...fu.followUpOptions],
    };
    return normalizeMappedDropdownValue(synthetic, val, tag);
  }
  return val;
}

function followUpStorageUnsettled(
  fu: DropdownOptionFollowUpDef,
  existing: string | undefined | null
): boolean {
  if (existing == null) return true;
  const val = String(existing).trim();
  if (!val) return true;
  if (fu.questionType === "checkbox" && val === "false") return true;
  return false;
}

function prefillFollowUpNodeIfEmpty(
  target: Record<string, string>,
  fu: DropdownOptionFollowUpDef,
  storageKey: string,
  row: Record<string, string>,
  storageKeyFor?: (valueKey: string) => string
): void {
  if (fu.dynamicValueSource === "front_office") return;
  const tag = normalizeDynamicTagId(fu.scrutinyLinkedDeedId);
  if (!tag) return;
  const existing = target[storageKey];
  if (!followUpStorageUnsettled(fu, existing)) return;
  const nextRaw =
    lookupRowTagValue(tag, row) ||
    String(row[tag] ?? "").trim() ||
    resolveTagValue(tag, row);
  if (!nextRaw) return;
  const next = normalizeFollowUpPrefillValue(fu, tag, nextRaw);
  if (!next) return;
  target[storageKey] = next;
  const keyFor = storageKeyFor ?? ((vk: string) => vk);
  (fu.nestedFollowUps || []).forEach((nested) =>
    prefillFollowUpNodeIfEmpty(target, nested, keyFor(nested.valueKey), row, storageKeyFor)
  );
}

function prefillFollowUpsForField(
  target: Record<string, string>,
  field: ChecklistFieldDef,
  row: Record<string, string>,
  storageKeyFor: (valueKey: string) => string,
  parentValue?: string
): void {
  if (!fieldHasConfiguredFollowUps(field) || !field.optionFollowUps?.length) return;
  const activeBranch =
    parentValue !== undefined ? triggerFollowBranchKeyForValue(field, parentValue) : "";
  field.optionFollowUps.forEach((fu) => {
    if (activeBranch && fu.whenOption !== activeBranch) return;
    prefillFollowUpNodeIfEmpty(target, fu, storageKeyFor(fu.valueKey), row, storageKeyFor);
  });
}

function mappedDropdownValueMatchesOptions(
  field: ChecklistFieldDef,
  value: string | undefined | null
): boolean {
  if (field.type !== "dropdown" || !field.options?.length) return true;
  const text = String(value ?? "").trim();
  if (!text) return false;
  const tag = resolveScrutinyFieldTag(field);
  const normalized = normalizeMappedDropdownValue(field, text, tag);
  return field.options.some((opt) => opt === normalized || opt === text);
}

function scrutinyPrefillTargetUnset(
  field: ChecklistFieldDef,
  value: string | undefined | null
): boolean {
  const text = value != null ? String(value).trim() : "";
  if (!text) return true;
  // Saved or user-entered answers always win — do not replace because options drifted.
  if (field.type === "dropdown" || field.type === "yes_no" || field.type === "checkbox") {
    return false;
  }
  return false;
}

export function prefillFieldIfEmpty(
  target: Record<string, string>,
  field: ChecklistFieldDef,
  row: Record<string, string>,
  force = false
): void {
  if (!fieldAllowsScrutinyPrefill(field)) return;
  if (isLegalHeirsMappedField(field)) {
    const heirs = parseLegalHeirsListFromScrutiny(row);
    if (heirs.length) mergeLegalHeirsPrefill(target, field.id, heirs);
    return;
  }
  const existing = target[field.id];
  if (!force && !scrutinyPrefillTargetUnset(field, existing)) return;
  // Saved yes/no, checkbox, and dropdown answers must survive document-type reconcile reloads.
  if (
    force &&
    (field.type === "yes_no" || field.type === "checkbox" || field.type === "dropdown")
  ) {
    return;
  }
  const tag = resolveScrutinyFieldTag(field);
  if (!tag) return;
  const nextRaw = resolveTagValue(tag, row);
  // Without force, skip empty document values so saved checklist answers remain.
  // With force, document is source of truth (including cleared values).
  if (!force && !nextRaw) return;
  let normalized: string;
  if (field.type === "dropdown") {
    normalized = normalizeMappedDropdownValue(field, nextRaw, tag);
  } else if (field.type === "checkbox") {
    normalized = isChecklistTruthyScalar(nextRaw) ? "true" : "false";
  } else if (field.type === "yes_no") {
    if (isChecklistTruthyScalar(nextRaw)) normalized = "Yes";
    else if (isChecklistFalsyScalar(nextRaw)) normalized = "No";
    else {
      if (!force) return;
      normalized = "";
    }
  } else {
    normalized = nextRaw || "";
  }
  if (isMissingSheetsField(field)) {
    normalized = normalizeMissingSheetsScalar(normalized);
  }
  if (!force && !normalized) return;
  target[field.id] = normalized;
}

function prefillFieldIfEmptyStrict(
  target: Record<string, string>,
  field: ChecklistFieldDef,
  row: Record<string, string>
): void {
  if (!fieldAllowsScrutinyPrefill(field)) return;
  const existing = target[field.id];
  if (existing != null && String(existing).trim() !== "") return;
  const tag = resolveScrutinyFieldTag(field);
  if (!tag) return;
  const next =
    String(row[tag] ?? "").trim() ||
    lookupRowTagValue(tag, row) ||
    resolveTagValue(tag, row);
  if (!next) return;
  const normalized =
    field.type === "dropdown"
      ? normalizeMappedDropdownValue(field, next, tag)
      : field.type === "checkbox"
        ? isChecklistTruthyScalar(next)
          ? "true"
          : "false"
        : next;
  if (!normalized) return;
  target[field.id] = normalized;
}

function normalizeMissingLodRow(item: any): Record<string, string> {
  if (!item || typeof item !== "object") return {};
  const row: Record<string, string> = {};
  Object.keys(item).forEach((k) => {
    const v = (item as any)[k];
    if (v != null && typeof v !== "object") {
      row[k] = String(v).trim();
    }
  });
  if (row.missingSheets != null) {
    row.missingSheets = normalizeMissingSheetsScalar(row.missingSheets);
  }
  reconcileMissingSheetsWhenCountsMatch(row);
  return row;
}

function isMissingLodTag(tag: string): boolean {
  return (
    tag === "fileNo" ||
    tag === "fileName" ||
    tag === "totalSheets" ||
    tag === "availableSheets" ||
    tag === "missingSheets"
  );
}

function isPreDisbursementTag(tag: string): boolean {
  return PRE_DISBURSEMENT_DYNAMIC_TAG_IDS.has(tag);
}

function isPostDisbursementTag(tag: string): boolean {
  return POST_DISBURSEMENT_DYNAMIC_TAG_IDS.has(tag);
}

function normalizeAddonScalarRow(addon: any): Record<string, string> | null {
  if (!addon || typeof addon !== "object") return null;
  const out: Record<string, string> = {};
  Object.keys(addon).forEach((k) => {
    const v = (addon as any)[k];
    if (v != null && typeof v !== "object") {
      out[k] = String(v).trim();
    }
  });
  return Object.keys(out).length ? out : null;
}

function normalizeDisbursementRow(item: any): Record<string, string> {
  if (!item || typeof item !== "object") return {};
  const row: Record<string, string> = {};
  Object.keys(item).forEach((k) => {
    const v = (item as any)[k];
    if (v != null && typeof v !== "object") {
      row[k] = String(v).trim();
    }
  });
  return row;
}

function applyDisbursementRepeatablePrefill(
  section: ChecklistSectionDef,
  mappedFields: ChecklistFieldDef[],
  sourceRows: Record<string, string>[],
  repeatableValues: Record<string, Record<string, string>[]>,
  groupSuffix: string
): void {
  if (!mappedFields.length || !sourceRows.length) return;
  const key = repeatableEntriesKey(section.id, groupSuffix);
  const existingRows = repeatableValues[key] || [];
  const rows: Record<string, string>[] = [];
  for (let i = 0; i < sourceRows.length; i++) {
    const source = sourceRows[i];
    const row: Record<string, string> =
      existingRows[i] && typeof existingRows[i] === "object"
        ? { ...existingRows[i] }
        : {};
    mappedFields.forEach((f) => {
      const existing = String(row[f.id] ?? "").trim();
      if (existing) return;
      const tag = normalizeDeedIdForSelect(f.scrutinyLinkedDeedId);
      if (!tag) return;
      const next = String(source[tag] ?? "").trim();
      if (next) row[f.id] = next;
    });
    if (groupSuffix === PRE_DISBURSEMENT_REPEATABLE_SUFFIX) {
      const id = source.preDisbursementId ?? source.pre_disburse_id;
      if (id != null && String(id).trim() !== "") {
        row[PRE_DISBURSEMENT_META_ID_KEY] = String(id).trim();
      }
    }
    if (groupSuffix === POST_DISBURSEMENT_REPEATABLE_SUFFIX) {
      const id = source.postDisbursementId ?? source.post_disburse_id;
      if (id != null && String(id).trim() !== "") {
        row[POST_DISBURSEMENT_META_ID_KEY] = String(id).trim();
      }
    }
    rows.push(row);
  }
  repeatableValues[key] = rows;
}

function surveyDocumentToScrutinyRow(doc: any): Record<string, any> {
  const normalized = normalizeScrutinyDeedRow(doc);
  const issuedBy = String(
    normalized.issued_by ?? normalized.issuedBy ?? normalized.legalHeirsObtained ?? ""
  ).trim();
  const date = String(normalized.date ?? normalized.dataVerified ?? normalized.dateVerified ?? "").trim();
  const docNo = String(normalized.doc_no ?? normalized.docNo ?? normalized.deedReference ?? "").trim();
  return {
    ...normalized,
    ds_id: doc?.ds_id,
    base_id: doc?.fd_id ?? doc?.base_id,
    fd_id: doc?.fd_id ?? doc?.base_id,
    position: doc?.position,
    nature_doc: doc?.nature_doc,
    deedReference: docNo,
    doc_no: docNo,
    docNo,
    issuedBy,
    issued_by: issuedBy,
    date,
    dataVerified: date,
    dateVerified: date,
    legalHeirsObtained: issuedBy,
  };
}

function documentsByNatureDocRows(scrutinyApiData: any, deedMasterId: number): any[] {
  const docMap = scrutinyApiData?.documentsByNatureDoc;
  if (!docMap || typeof docMap !== "object") return [];
  const rows = docMap[deedMasterId] ?? docMap[String(deedMasterId)];
  return Array.isArray(rows) ? rows : [];
}

function isScrutinyDocumentRow(item: any): boolean {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  return item.ds_id != null || item.base_id != null || item.fd_id != null;
}

function walkScrutinyDocumentRows(node: any, visit: (row: any) => void, depth = 0): void {
  if (!node || depth > 10) return;
  if (Array.isArray(node)) {
    node.forEach((item) => {
      if (isScrutinyDocumentRow(item)) visit(item);
    });
    return;
  }
  if (typeof node === "object") {
    Object.values(node).forEach((value) => walkScrutinyDocumentRows(value, visit, depth + 1));
  }
}

/** Collect typed scrutiny rows from any API section for documents of this nature_doc. */
function rowsFromAllScrutinyArrays(scrutinyApiData: any, deedMasterId: number): any[] {
  const dsIds = dsIdsForDeedMasterFromSurvey(scrutinyApiData, deedMasterId);
  const natureRows = documentsByNatureDocRows(scrutinyApiData, deedMasterId);
  natureRows.forEach((row) => {
    const ds = Number(row?.ds_id ?? 0);
    if (ds > 0) dsIds.add(ds);
  });
  if (!dsIds.size) return [];
  const out: any[] = [];
  const seen = new Set<number>();
  walkScrutinyDocumentRows(scrutinyApiData, (row) => {
    const ds = Number(row?.ds_id ?? 0);
    if (!ds || !dsIds.has(ds) || seen.has(ds)) return;
    const nature = Number(row?.nature_doc ?? 0);
    if (nature && nature !== Number(deedMasterId)) return;
    seen.add(ds);
    out.push(row);
  });
  return out;
}

function surveyDocumentsForDeedMaster(scrutinyApiData: any, deedMasterId: number): any[] {
  const docs = Array.isArray(scrutinyApiData?.surveyDocuments)
    ? scrutinyApiData.surveyDocuments
    : [];
  return docs
    .filter((d) => Number(d?.nature_doc) === Number(deedMasterId))
    .map(surveyDocumentToScrutinyRow);
}

function dsIdsForDeedMasterFromSurvey(scrutinyApiData: any, deedMasterId: number): Set<number> {
  const out = new Set<number>();
  surveyDocumentsForDeedMaster(scrutinyApiData, deedMasterId).forEach((row) => {
    const ds = Number(row?.ds_id ?? 0);
    if (ds > 0) out.add(ds);
  });
  return out;
}

function rowsFromTypedScrutinySources(
  scrutinyApiData: any,
  deedMasterId: number,
  deedMasterList?: DeedMasterRowLite[]
): any[] {
  const id = Number(deedMasterId);
  const natureRows = documentsByNatureDocRows(scrutinyApiData, id);
  const apiKey = apiKeyForDeedMasterId(id, deedMasterList);
  const deedRows =
    apiKey && Array.isArray(scrutinyApiData?.deed?.[apiKey])
      ? scrutinyApiData.deed[apiKey]
      : [];
  const section = SCRUTINY_SECTION_BY_DEED_MASTER_ID[id];
  let sectionRows: any[] = [];
  if (section) {
    const arr = scrutinyApiData?.[section.root]?.[section.key];
    if (Array.isArray(arr)) {
      sectionRows = section.filterByNatureDoc
        ? arr.filter((row: any) => {
            const dsIds = dsIdsForDeedMasterFromSurvey(scrutinyApiData, id);
            if (!dsIds.size) return Number(row?.nature_doc ?? 0) === id;
            return dsIds.has(Number(row?.ds_id ?? 0));
          })
        : arr;
    }
  }
  const scannedRows = rowsFromAllScrutinyArrays(scrutinyApiData, id);
  return mergeScrutinyRowSets([natureRows, deedRows, sectionRows, scannedRows]);
}

function rowIdentityKey(row: any): string {
  const ds = Number(row?.ds_id ?? 0);
  if (ds > 0) return `ds:${ds}`;
  const base = Number(row?.base_id ?? row?.fd_id ?? 0);
  const pos = Number(row?.position ?? 0);
  if (base > 0) return `base:${base}::pos:${pos}`;
  const docNo = String(row?.deedReference ?? row?.doc_no ?? "").trim().toLowerCase();
  return docNo ? `doc:${docNo}` : "";
}

/** Document columns owned by `TBL_DOC_SCURTY` / documentsByNatureDoc — never clobber with empty. */
const DOC_SCURTY_SCALAR_KEYS = new Set([
  "extent",
  "unit",
  "issuedby",
  "issued_by",
  "favour",
  "favourof",
  "doc_no",
  "docno",
  "deedreference",
  "date",
  "current_survey",
  "old_surevey",
  "old_survey_no",
  "new_survey_no",
  "totalsheets",
  "availablesheets",
  "sheet_count",
  "sheetcount",
  "missingsheets",
  "reg",
  "registered",
  "remark",
]);

function normalizeDocScalarKey(key: string): string {
  return String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "");
}

/**
 * Soft-merge later rows onto earlier ones. Empty strings must not wipe
 * documentsByNatureDoc scalars (e.g. extent) filled from TBL_DOC_SCURTY.
 */
function mergeScrutinyDocumentRows(prev: any, next: any): any {
  const out = { ...prev };
  Object.keys(next || {}).forEach((k) => {
    const v = next[k];
    if (v == null) return;
    if (typeof v === "object") {
      out[k] = v;
      return;
    }
    const str = String(v).trim();
    const prevStr = out[k] == null ? "" : String(out[k]).trim();
    if (
      str === "" &&
      prevStr !== "" &&
      DOC_SCURTY_SCALAR_KEYS.has(normalizeDocScalarKey(k))
    ) {
      return;
    }
    out[k] = typeof v === "string" ? str || v : v;
  });
  return out;
}

function mergeScrutinyRowSets(rowSets: any[][]): any[] {
  const mergedByKey = new Map<string, any>();
  rowSets.forEach((rows) => {
    rows.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const key = rowIdentityKey(row);
      if (!key) return;
      const prev = mergedByKey.get(key);
      mergedByKey.set(key, prev ? mergeScrutinyDocumentRows(prev, row) : { ...row });
    });
  });
  return [...mergedByKey.values()];
}

function mergeSurveyAndTypedScrutinyRows(surveyRows: any[], typedRows: any[]): any[] {
  return mergeScrutinyRowSets([surveyRows, typedRows]);
}

function scrutinyRowsForDeed(
  scrutinyApiData: any,
  deedMasterId: number | undefined,
  deedMasterList?: DeedMasterRowLite[]
): any[] {
  if (!deedMasterId || !scrutinyApiData) return [];
  const id = Number(deedMasterId);
  // Dynamic Checklist must be strict: show only document types that actually
  // exist in this file (`nature_doc` / surveyDocuments), then enrich those rows.
  const natureRows = documentsByNatureDocRows(scrutinyApiData, id);
  const surveyRows = surveyDocumentsForDeedMaster(scrutinyApiData, id);
  const baseRows = mergeScrutinyRowSets([natureRows, surveyRows]);
  if (!baseRows.length) return [];

  const dsIds = new Set<number>();
  baseRows.forEach((row) => {
    const ds = Number(row?.ds_id ?? 0);
    if (ds > 0) dsIds.add(ds);
  });

  const typedRows = rowsFromTypedScrutinySources(scrutinyApiData, id, deedMasterList).filter((row) => {
    const ds = Number(row?.ds_id ?? 0);
    if (dsIds.size) return ds > 0 && dsIds.has(ds);
    return false;
  });

  // Typed legacy arrays enrich first; documentsByNatureDoc / surveyDocuments win last
  // so ScrutinyForm fields (extent, favour, doc_no, …) stay the source of truth.
  return sortScrutinyDeedRows(mergeScrutinyRowSets([typedRows, baseRows]));
}

function scrutinyDeedRowPosition(row: any): number {
  if (!row || typeof row !== "object") return Number.MAX_SAFE_INTEGER;
  const raw = row.position ?? row.pos ?? row.position_order;
  const n = Number(raw);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

function sortScrutinyDeedRows(rows: any[]): any[] {
  return [...rows].sort((a, b) => {
    const posDiff = scrutinyDeedRowPosition(a) - scrutinyDeedRowPosition(b);
    if (posDiff !== 0) return posDiff;
    const dsA = Number(a?.ds_id ?? 0);
    const dsB = Number(b?.ds_id ?? 0);
    if (Number.isFinite(dsA) && Number.isFinite(dsB) && dsA !== dsB) {
      return dsA - dsB;
    }
    const docA = String(a?.deedReference ?? a?.docNo ?? "").trim();
    const docB = String(b?.deedReference ?? b?.docNo ?? "").trim();
    return docA.localeCompare(docB);
  });
}

function repeatableRowSavePosition(row: Record<string, string> | undefined | null): number {
  if (!row || typeof row !== "object") return Number.MAX_SAFE_INTEGER;
  const raw = String(row[REPEATABLE_POSITION_META_KEY] ?? "").trim();
  const n = Number(raw);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

/** Sort deed repeatable rows by List Of Documents save order (`position`). */
export function sortRepeatableRowsBySaveOrder(
  rows: Record<string, string>[]
): Record<string, string>[] {
  return [...rows].sort((a, b) => {
    const posDiff = repeatableRowSavePosition(a) - repeatableRowSavePosition(b);
    if (posDiff !== 0) return posDiff;
    const docA = String(a[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
    const docB = String(b[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
    return docA.localeCompare(docB);
  });
}

/** Row indices in save-order for rendering without mutating stored array order. */
export function sortRepeatableRowIndices(rows: Record<string, string>[]): number[] {
  return rows
    .map((row, idx) => ({ row, idx }))
    .sort((a, b) => {
      const posDiff = repeatableRowSavePosition(a.row) - repeatableRowSavePosition(b.row);
      if (posDiff !== 0) return posDiff;
      const docA = String(a.row[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
      const docB = String(b.row[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
      return docA.localeCompare(docB);
    })
    .map(({ idx }) => idx);
}

function minSaveOrderForDeedGate(
  section: ChecklistSectionDef,
  gate: ChecklistFieldDef,
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData?: any,
  deedMasterList?: DeedMasterRowLite[]
): number {
  const group = deedLinkedGroups(section).find(
    (g) => String(g.deedMasterId) === String(gate.deedMasterId)
  );
  let min = Number.MAX_SAFE_INTEGER;
  if (group) {
    const key = repeatableEntriesKey(section.id, group.id);
    sortRepeatableRowsBySaveOrder(repeatableValues[key] || []).forEach((row) => {
      const pos = repeatableRowSavePosition(row);
      if (pos < min) min = pos;
    });
  }
  if (min === Number.MAX_SAFE_INTEGER && scrutinyApiData && gate.deedMasterId) {
    scrutinyRowsForDeed(scrutinyApiData, Number(gate.deedMasterId), deedMasterList).forEach((row) => {
      const pos = scrutinyDeedRowPosition(row);
      if (pos < min) min = pos;
    });
  }
  if (min === Number.MAX_SAFE_INTEGER) {
    return 100000 + Number(gate.order ?? 0);
  }
  return min;
}

/**
 * Order deed yes/no gates by file document save order (matches legal report / List Of Documents).
 * Deed types appear when their first document appears in the saved sequence.
 */
export function sortDeedGatesBySaveOrder(
  gates: ChecklistFieldDef[],
  section: ChecklistSectionDef,
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData?: any,
  deedMasterList?: DeedMasterRowLite[]
): ChecklistFieldDef[] {
  return [...gates].sort((a, b) => {
    const posDiff =
      minSaveOrderForDeedGate(section, a, repeatableValues, scrutinyApiData, deedMasterList) -
      minSaveOrderForDeedGate(section, b, repeatableValues, scrutinyApiData, deedMasterList);
    if (posDiff !== 0) return posDiff;
    return Number(a.order ?? 0) - Number(b.order ?? 0);
  });
}

/** One scrutinized document row in List Of Documents save order (may repeat deed types). */
export interface DeedDocumentEntryInOrder {
  position: number;
  gate: ChecklistFieldDef;
  group: ChecklistRepeatableGroupDef;
  rowIndex: number;
  row: Record<string, string>;
  displayIndex: number;
}

/**
 * Flatten all deed documents across types and sort by save order (legal report sequence).
 * Example: Sale Deed → GPA → Sale Deed when that is the file document order.
 */
export function collectDeedDocumentEntriesInSaveOrder(
  section: ChecklistSectionDef,
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData?: any,
  deedMasterList?: DeedMasterRowLite[]
): DeedDocumentEntryInOrder[] {
  const gates = sectionGateFields(section);
  const entries: DeedDocumentEntryInOrder[] = [];

  gates.forEach((gate) => {
    const group = deedLinkedGroups(section).find(
      (g) => String(g.deedMasterId) === String(gate.deedMasterId)
    );
    if (!group) return;
    const key = repeatableEntriesKey(section.id, group.id);
    const allRows = repeatableValues[key] || [];

    if (scrutinyApiData && gate.deedMasterId) {
      const scrutinyRows = scrutinyRowsForDeed(
        scrutinyApiData,
        Number(gate.deedMasterId),
        deedMasterList
      );
      if (!scrutinyRows.length) return;

      const existingByIdentity = indexRepeatableRowsByIdentity(allRows);
      scrutinyRows.forEach((apiRow, rowIndex) => {
        const normalized = normalizeScrutinyDeedRow(apiRow);
        const identity = scrutinyDeedRowIdentity(normalized);
        const matchedExisting = identity ? existingByIdentity.get(identity) : undefined;
        const row =
          matchedExisting && typeof matchedExisting === "object"
            ? { ...matchedExisting }
            : allRows[rowIndex] && typeof allRows[rowIndex] === "object"
              ? { ...allRows[rowIndex] }
              : emptyRepeatableRow(group.fields);
        applyRepeatableRowMetaFromScrutiny(row, normalized);
        entries.push({
          position: scrutinyDeedRowPosition(apiRow),
          gate,
          group,
          rowIndex,
          row,
          displayIndex: 0,
        });
      });
      return;
    }

    if (allRows.length) {
      sortRepeatableRowIndices(allRows).forEach((rowIndex) => {
        const row = allRows[rowIndex];
        entries.push({
          position: repeatableRowSavePosition(row),
          gate,
          group,
          rowIndex,
          row,
          displayIndex: 0,
        });
      });
      return;
    }
  });

  entries.sort((a, b) => {
    const posDiff = a.position - b.position;
    if (posDiff !== 0) return posDiff;
    const docA = String(a.row[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
    const docB = String(b.row[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
    if (docA && docB) return docA.localeCompare(docB);
    return Number(a.gate.order ?? 0) - Number(b.gate.order ?? 0);
  });

  entries.forEach((entry, index) => {
    entry.displayIndex = index;
  });
  return entries;
}

export function deedDocumentEntryVisible(
  entry: DeedDocumentEntryInOrder,
  checklistValues: Record<string, string>
): boolean {
  const gateVal = String(checklistValues[entry.gate.id] ?? "").trim();
  const hasDoc = String(entry.row[REPEATABLE_DOC_NO_META_KEY] ?? "").trim() !== "";
  const hasIdentity =
    String(entry.row[REPEATABLE_DS_ID_META_KEY] ?? "").trim() !== "" ||
    String(entry.row[REPEATABLE_DOC_BASE_ID_META_KEY] ?? "").trim() !== "";
  const hasValues = entry.group.fields.some((f) =>
    isLegalHeirsMappedField(f)
      ? legalHeirsRowHasValue(entry.row, f.id)
      : isDocumentScheduleMappedField(f)
        ? documentScheduleFieldRowHasValue(
            entry.row,
            f,
            documentScheduleFieldsInGroup(entry.group.fields)
          )
        : isChecklistAnswerFilled(f.type, entry.row[f.id])
  );
  return gateVal === "Yes" || hasDoc || hasIdentity || hasValues;
}

function scrutinyDeedRowIdentity(source?: Record<string, unknown> | null): string {
  if (!source || typeof source !== "object") return "";
  // Prefer ds_id — stable across checklist saves and matches ScrutinyForm document rows.
  const dsId = String(source[REPEATABLE_DS_ID_META_KEY] ?? source.ds_id ?? source.scrut_idfk ?? "").trim();
  if (dsId) return `ds:${dsId}`;
  const docNo = String(
    source[REPEATABLE_DOC_NO_META_KEY] ??
      source.deedReference ??
      source.docNo ??
      source.doc_no ??
      ""
  )
    .trim()
    .toLowerCase();
  if (docNo) return `doc:${docNo}`;
  const baseId = String(
    source[REPEATABLE_DOC_BASE_ID_META_KEY] ?? source.base_id ?? source.fd_id ?? ""
  ).trim();
  if (baseId) return `base:${baseId}`;
  return "";
}

function scrutinyDeedRowIdentityKeys(source?: Record<string, unknown> | null): string[] {
  if (!source || typeof source !== "object") return [];
  const keys: string[] = [];
  const dsId = String(source[REPEATABLE_DS_ID_META_KEY] ?? source.ds_id ?? source.scrut_idfk ?? "").trim();
  if (dsId) keys.push(`ds:${dsId}`);
  const docNo = String(
    source[REPEATABLE_DOC_NO_META_KEY] ??
      source.deedReference ??
      source.docNo ??
      source.doc_no ??
      ""
  )
    .trim()
    .toLowerCase();
  if (docNo) keys.push(`doc:${docNo}`);
  const baseId = String(
    source[REPEATABLE_DOC_BASE_ID_META_KEY] ?? source.base_id ?? source.fd_id ?? ""
  ).trim();
  if (baseId) keys.push(`base:${baseId}`);
  const primary = scrutinyDeedRowIdentity(source);
  if (primary && !keys.includes(primary)) keys.unshift(primary);
  return keys;
}

function indexRepeatableRowsByIdentity(
  rows: Record<string, string>[]
): Map<string, Record<string, string>> {
  const out = new Map<string, Record<string, string>>();
  rows.forEach((row) => {
    scrutinyDeedRowIdentityKeys(row as Record<string, unknown>).forEach((key) => {
      if (key && !out.has(key)) out.set(key, row);
    });
  });
  return out;
}

function applyRepeatableRowMetaFromScrutiny(
  target: Record<string, string>,
  normalized: Record<string, string>
): void {
  const docNo = extractScrutinyDocumentNumber(normalized);
  const baseId = pickByNormalizedKey(normalized, [
    "base_id",
    "baseid",
    "base_idfk",
    "file_doc_id",
    "fd_id",
  ]);
  const dsId = pickByNormalizedKey(normalized, ["ds_id", "scrut_idfk", "scrut_id"]);
  const position = pickByNormalizedKey(normalized, ["position", "pos", "position_order"]);
  if (docNo) target[REPEATABLE_DOC_NO_META_KEY] = docNo;
  if (baseId) target[REPEATABLE_DOC_BASE_ID_META_KEY] = baseId;
  if (dsId) target[REPEATABLE_DS_ID_META_KEY] = dsId;
  if (position) target[REPEATABLE_POSITION_META_KEY] = position;
}

function extractScrutinyDocumentNumber(row: Record<string, string>): string {
  const candidates = [
    row.deedReference,
    row.doc_no,
    row.docNo,
    row[REPEATABLE_DOC_NO_META_KEY],
  ];
  for (const v of candidates) {
    const text = String(v ?? "").trim();
    if (text) return text;
  }
  return "";
}

function pickByNormalizedKey(row: Record<string, string>, acceptedKeys: string[]): string {
  const accepted = new Set(acceptedKeys.map((k) => k.trim().toLowerCase()));
  for (const rawKey of Object.keys(row || {})) {
    const normKey = String(rawKey).trim().toLowerCase();
    if (!accepted.has(normKey)) continue;
    const value = String((row as any)[rawKey] ?? "").trim();
    if (value) return value;
  }
  return "";
}

/** Merge per-document sheet counts from `missingLOD.sheets[]` when not on the deed row. */
function mergeMissingLodSheetIntoRow(
  normalized: Record<string, string>,
  missingLodRows: Record<string, string>[]
): Record<string, string> {
  if (!missingLodRows.length) return normalized;
  const docNo = extractScrutinyDocumentNumber(normalized);
  if (!docNo) return normalized;
  const wanted = docNo.trim().toLowerCase();
  const match = missingLodRows.find(
    (row) => String(row.fileNo ?? "").trim().toLowerCase() === wanted
  );
  if (!match) return normalized;
  const out = { ...normalized };
  if (!String(out.totalSheets ?? "").trim() && String(match.totalSheets ?? "").trim()) {
    out.totalSheets = String(match.totalSheets).trim();
  }
  if (!String(out.availableSheets ?? "").trim() && String(match.availableSheets ?? "").trim()) {
    out.availableSheets = String(match.availableSheets).trim();
  }
  const missing = normalizeMissingSheetsScalar(match.missingSheets);
  if (!String(out.missingSheets ?? "").trim() && missing) {
    out.missingSheets = missing;
  }
  reconcileMissingSheetsWhenCountsMatch(out);
  return out;
}

function applyGroupScrutinyPrefill(
  section: ChecklistSectionDef,
  group: ChecklistRepeatableGroupDef,
  repeatableValues: Record<string, Record<string, string>[]>,
  checklistValues: Record<string, string>,
  scrutinyApiData: any,
  deedMasterList?: DeedMasterRowLite[],
  missingLodRows: Record<string, string>[] = [],
  options?: { forceMappedFields?: boolean }
): void {
  const key = repeatableEntriesKey(section.id, group.id);
  const rawRows = scrutinyRowsForDeed(scrutinyApiData, group.deedMasterId, deedMasterList);
  if (!rawRows.length) {
    repeatableValues[key] = [];
    return;
  }

  const min = Math.max(1, group.minInstances ?? 1);
  const targetCount = Math.max(min, rawRows.length);
  const existing = repeatableValues[key] || [];
  const existingByIdentity = indexRepeatableRowsByIdentity(existing);
  const rows: Record<string, string>[] = [];

  const groupDeedId = Number(group.deedMasterId ?? 0);

  for (let i = 0; i < targetCount; i++) {
    const normalized = normalizeScrutinyDeedRow(rawRows[i] || rawRows[0]);
    const enriched = mergeMissingLodSheetIntoRow(normalized, missingLodRows);
    const rowNatureDoc = Number(
      enriched.nature_doc ?? normalized.nature_doc ?? rawRows[i]?.nature_doc ?? 0
    );
    const identityKeys = scrutinyDeedRowIdentityKeys(enriched);
    let matchedExisting: Record<string, string> | undefined;
    for (let k = 0; k < identityKeys.length; k++) {
      matchedExisting = existingByIdentity.get(identityKeys[k]);
      if (matchedExisting) break;
    }
    if (
      matchedExisting &&
      groupDeedId > 0 &&
      rowNatureDoc > 0 &&
      rowNatureDoc !== groupDeedId
    ) {
      matchedExisting = undefined;
    }
    const base =
      matchedExisting && typeof matchedExisting === "object"
        ? { ...matchedExisting }
        : existing[i] && typeof existing[i] === "object"
          ? { ...existing[i] }
          : emptyRepeatableRow(group.fields);
    applyRepeatableRowMetaFromScrutiny(base, enriched);
    const scheduleFields = documentScheduleFieldsInGroup(group.fields);
    const forceMapped = options?.forceMappedFields === true;
    if (scheduleFields.length) {
      mergeDocumentSchedulePrefill(
        base,
        scheduleFields,
        parseDocumentScheduleSetsFromScrutiny(enriched),
        { preferDocument: forceMapped }
      );
    }
    group.fields.forEach((f) => {
      if (isDocumentScheduleMappedField(f)) return;
      prefillFieldIfEmpty(base, f, enriched, forceMapped);
      // Force path: always harden text/date/remarks from the live document row
      // (covers stale checklist snapshot values like Patta extent).
      if (forceMapped && (f.type === "text" || f.type === "date" || f.type === "remarks")) {
        const tag = resolveScrutinyFieldTag(f);
        if (tag && fieldAllowsScrutinyPrefill(f) && !isLegalHeirsMappedField(f)) {
          const nextRaw = resolveTagValue(tag, enriched);
          if (nextRaw != null) {
            let normalized = String(nextRaw);
            if (isMissingSheetsField(f)) {
              normalized = normalizeMissingSheetsScalar(normalized);
            }
            base[f.id] = normalized;
          }
        }
      }
      prefillFollowUpsForField(
        checklistValues,
        f,
        enriched,
        (vk) => repeatableRowFollowUpStorageKey(section.id, group.id, i, vk),
        base[f.id]
      );
    });
    rows.push(base);
  }

  repeatableValues[key] = rows;
}

export interface ScrutinyPrefillOptions {
  autoOpenDeedGates?: boolean;
  deedMasterList?: DeedMasterRowLite[];
  /** Process checklist: prefill survey fields from titleDeed row matching this fd_id. */
  activeDocumentFdId?: string | number | null;
}

/** Pick titleDeed scrutiny row for a document, or first row as fallback. */
export function titleDeedRowForDocument(
  surveyApi: any,
  activeDocumentFdId?: string | number | null
): Record<string, string> {
  if (!surveyApi || typeof surveyApi !== "object") return {};
  const rows = Array.isArray(surveyApi.titleDeed) ? surveyApi.titleDeed : [];
  const fdId = String(activeDocumentFdId ?? "").trim();
  if (fdId) {
    const match = rows.find((r: any) => String(r?.fd_id ?? "").trim() === fdId);
    if (match) return normalizeScrutinyDeedRow(match);
  }
  if (rows.length) return normalizeScrutinyDeedRow(rows[0]);
  return normalizeSurveyVerificationRow(surveyApi);
}

function prefillSurveyMappedFieldIfEmpty(
  target: Record<string, string>,
  field: ChecklistFieldDef,
  source: Record<string, string>,
  fallbackSource?: Record<string, string>,
  force?: boolean
): void {
  if (field.dynamicValueSource === "front_office") return;
  if (field.type !== "text" && field.type !== "date" && field.type !== "remarks") return;
  const fieldId = field.id;
  const existing = target[fieldId];
  // Without force, saved checklist answers win. With force, re-sync from General Detail.
  if (!force && existing != null && String(existing).trim() !== "") return;
  const tag = resolveSurveyMappedFieldTag(field);
  if (!tag) return;
  let val = mapSurveyTagToSourceRow(tag, source);
  if (!val && fallbackSource) {
    val = mapSurveyTagToSourceRow(tag, fallbackSource);
  }
  if (!force && !val) return;
  if (force || val) {
    target[fieldId] = val || "";
  }
}

/** Prefill mapped follow-up answers (stored in `checklistValues`, including repeatable row keys). */
export function applyScrutinyFollowUpPrefill(
  sections: ChecklistSectionDef[],
  checklistValues: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData: any,
  deedMasterList?: DeedMasterRowLite[],
  missingLodRows: Record<string, string>[] = []
): void {
  sections.forEach((section) => {
    deedLinkedGroups(section).forEach((group) => {
      const key = repeatableEntriesKey(section.id, group.id);
      const rawRows = scrutinyRowsForDeed(scrutinyApiData, group.deedMasterId, deedMasterList);
      const rows = repeatableValues[key] || [];
      rows.forEach((base, i) => {
        const normalized = normalizeScrutinyDeedRow(rawRows[i] || rawRows[0] || {});
        const enriched = mergeMissingLodSheetIntoRow(normalized, missingLodRows);
        group.fields.forEach((f) => {
          prefillFollowUpsForField(
            checklistValues,
            f,
            enriched,
            (vk) => repeatableRowFollowUpStorageKey(section.id, group.id, i, vk),
            base[f.id]
          );
        });
      });
    });

    section.fields.forEach((f) => {
      if (f.deedMasterId != null || !fieldHasConfiguredFollowUps(f)) return;
      if (!resolveScrutinyFieldTag(f)) return;
      for (const deedId of Object.keys(DEED_MASTER_ID_TO_API_KEY).map(Number)) {
        const rows = scrutinyRowsForDeed(scrutinyApiData, deedId, deedMasterList);
        if (!rows.length) continue;
        prefillFollowUpsForField(
          checklistValues,
          f,
          normalizeScrutinyDeedRow(rows[0]),
          (vk) => vk,
          checklistValues[f.id]
        );
        break;
      }
    });
  });
}

/** Prefill survey verification nature groups and hierarchy from scrutiny API. */
export function applySurveyVerificationPrefill(
  sections: ChecklistSectionDef[],
  checklistValues: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData: any,
  options?: {
    activeDocumentFdId?: string | number | null;
    propertyDetails?: Record<string, string> | null;
    generalDetailsByDocument?: Record<string, GeneralDetailsDocumentHierarchy> | null;
    forceHierarchyFields?: boolean;
  }
): void {
  const propertyDetails = options?.propertyDetails ?? null;
  const generalDetailsByDocument = options?.generalDetailsByDocument ?? null;
  const hasGeneralDetailsHierarchy =
    generalDetailsByDocument != null && Object.keys(generalDetailsByDocument).length > 0;
  const resolvedFdId =
    resolveSurveyDocumentFdWithSchedules(options?.activeDocumentFdId, scrutinyApiData) ||
    resolveSurveyDocumentFdId(options?.activeDocumentFdId, scrutinyApiData) ||
    String(options?.activeDocumentFdId ?? "").trim();
  const titleDeedRow = titleDeedRowForDocument(
    scrutinyApiData?.surveyNoVerification,
    resolvedFdId || options?.activeDocumentFdId
  );
  const hasTitleDeedRow = Object.keys(titleDeedRow).length > 0;
  const forceHierarchyFields = options?.forceHierarchyFields === true;

  sections.forEach((section) => {
    if (sectionFlowMode(section) !== "survey_verification") return;

    const natureKeys = resolveSurveyNaturePropertyKeys(
      null,
      propertyDetails,
      generalDetailsByDocument,
      resolvedFdId || null
    );

    if (hasTitleDeedRow && !hasGeneralDetailsHierarchy) {
      resolveVisibleSurveyNatureGroups(section, natureKeys).forEach((g) => {
        g.fields.forEach((f) => prefillFieldIfEmpty(checklistValues, f, titleDeedRow));
      });
    }

    if (hasGeneralDetailsHierarchy) {
      const surveyDocs = Array.isArray(scrutinyApiData?.surveyDocuments)
        ? scrutinyApiData.surveyDocuments
        : [];
      const docNoByFd = new Map<string, string>();
      surveyDocs.forEach((d: any) => {
        const fd = String(d?.fd_id ?? "").trim();
        if (fd) docNoByFd.set(fd, String(d?.doc_no ?? "").trim());
      });
      const combined = {
        schedule: [] as Record<string, string>[],
        survey: [] as Record<string, string>[],
        boundary: [] as Record<string, string>[],
        measurement: [] as Record<string, string>[],
      };
      Object.keys(generalDetailsByDocument!).forEach((fdKey) => {
        const flat = flattenSurveyHierarchyRows(
          generalDetailsByDocument![fdKey],
          fdKey,
          docNoByFd.get(fdKey)
        );
        combined.schedule.push(...flat.schedule);
        combined.survey.push(...flat.survey);
        combined.boundary.push(...flat.boundary);
        combined.measurement.push(...flat.measurement);
      });
      applySurveyHierarchyPrefill(section, repeatableValues, combined, {
        // When General Details hierarchy is present, do not fallback to
        // surveyNoVerification.titleDeed for hierarchy rows. That fallback can
        // incorrectly mirror values across Old/New Survey, TS, and Natham fields.
        titleDeedRow: null,
        forceFields: forceHierarchyFields,
      });

      const fdFilter = resolvedFdId;
      const scheduleRows = fdFilter
        ? combined.schedule.filter(
            (r) => String(r[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() === fdFilter
          )
        : combined.schedule;
      scheduleRows.forEach((schedRow) => {
        const rowFd = String(schedRow[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim();
        const rowSchedId = String(schedRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim();
        if (!rowFd || !rowSchedId) return;
        const natureKey = String(schedRow[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
        const visibleGroups = natureKey
          ? resolveVisibleSurveyNatureGroups(section, new Set([natureKey]))
          : resolveVisibleSurveyNatureGroups(section, natureKeys);
        const prefillSource = buildScheduleNaturePrefillSource(schedRow, combined);
        const hierarchyFieldIds = new Set(
          surveyHierarchyLinkedGroups(section).flatMap((hg) =>
            (hg.fields || []).map((hf) => hf.id)
          )
        );
        visibleGroups.forEach((g) => {
          g.fields.forEach((f) => {
            // Hierarchy groups already own per-survey/boundary/measurement values.
            // Prefilling the same field into schedule-scoped keys (primary/last survey)
            // would later overwrite every survey on checklist save.
            if (hierarchyFieldIds.has(f.id)) return;
            const scopedKey = surveyNatureFieldScopeKey(rowFd, rowSchedId, f.id);
            prefillSurveyMappedFieldIfEmpty(
              checklistValues,
              { ...f, id: scopedKey },
              prefillSource,
              undefined,
              forceHierarchyFields
            );
          });
        });
      });
    }
  });
}

/** Map scrutiny document row id (`ds_id`) to current `nature_doc` / deed type. */
function buildDsIdToNatureDocMap(scrutinyApiData: any): Map<number, number> {
  const out = new Map<number, number>();
  const surveyDocs = Array.isArray(scrutinyApiData?.surveyDocuments)
    ? scrutinyApiData.surveyDocuments
    : [];
  surveyDocs.forEach((doc: any) => {
    const dsId = Number(doc?.ds_id ?? 0);
    const natureDoc = Number(doc?.nature_doc ?? 0);
    if (dsId > 0 && natureDoc > 0) out.set(dsId, natureDoc);
  });
  const byNature = scrutinyApiData?.documentsByNatureDoc;
  if (byNature && typeof byNature === "object") {
    Object.keys(byNature).forEach((natureKey) => {
      const natureDoc = Number(natureKey);
      const rows = Array.isArray(byNature[natureKey]) ? byNature[natureKey] : [];
      rows.forEach((row: any) => {
        const dsId = Number(row?.ds_id ?? 0);
        if (dsId > 0 && natureDoc > 0) out.set(dsId, natureDoc);
      });
    });
  }
  return out;
}

/**
 * Sync deed document gates/rows from scrutiny when document type changes
 * (e.g. Partition Deed → Gift Settlement Deed on the same ds_id).
 */
export function applyDeedDocumentVerificationPrefill(
  sections: ChecklistSectionDef[],
  checklistValues: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData: any,
  options?: {
    deedMasterList?: DeedMasterRowLite[];
    /** Close stale gates and open gates that match current scrutiny document types. */
    reconcileGates?: boolean;
    /**
     * Overwrite checklist text/date mapped fields from current scrutiny
     * (document → checklist sync after ScrutinyForm edits).
     */
    forceMappedFields?: boolean;
  }
): void {
  if (!scrutinyApiData) return;
  const missingLodRows = Array.isArray(scrutinyApiData?.missingLOD?.sheets)
    ? (scrutinyApiData.missingLOD.sheets as any[]).map((r) => normalizeMissingLodRow(r))
    : [];
  const dsIdToNatureDoc = buildDsIdToNatureDocMap(scrutinyApiData);
  // Explicit forceMappedFields controls overwrite. reconcileGates alone must not
  // force-rewrite text fields (that path also sets gates and previously bundled force).
  const forceMappedFields = options?.forceMappedFields === true;

  sections.forEach((section) => {
    // Prefer document_types, but also sync deed-linked groups under "checklist"
    // flow when the master still uses gates + document types.
    const groups = deedLinkedGroups(section);
    if (!groups.length) return;
    const flow = sectionFlowMode(section);
    if (flow === "survey_verification" || flow === "ec_verification" || flow === "bank_product") {
      return;
    }

    groups.forEach((group) => {
      const key = repeatableEntriesKey(section.id, group.id);
      const deedId = Number(group.deedMasterId ?? 0);
      const rows = (repeatableValues[key] || []).filter((row) => {
        const dsId = Number(row[REPEATABLE_DS_ID_META_KEY] ?? 0);
        if (!dsId || !deedId) return true;
        const natureDoc = dsIdToNatureDoc.get(dsId);
        if (!natureDoc) return true;
        return natureDoc === deedId;
      });
      repeatableValues[key] = rows;
    });

    if (options?.reconcileGates) {
      sectionGateFields(section).forEach((gate) => {
        const group = groups.find((g) => String(g.deedMasterId) === String(gate.deedMasterId));
        const scrutinyRows = group
          ? scrutinyRowsForDeed(scrutinyApiData, group.deedMasterId, options?.deedMasterList)
          : [];
        checklistValues[gate.id] = scrutinyRows.length ? "Yes" : "No";
      });
    }

    groups.forEach((group) => {
      applyGroupScrutinyPrefill(
        section,
        group,
        repeatableValues,
        checklistValues,
        scrutinyApiData,
        options?.deedMasterList,
        missingLodRows,
        { forceMappedFields }
      );
    });
  });
}

/**
 * Prefill checklist / repeatable values from scrutiny (`get_dynamic_verificationchecklist.php`).
 * Only fills empty cells so saved checklist answers are never overwritten.
 */
export function applyScrutinyTagPrefill(
  sections: ChecklistSectionDef[],
  runtime: DynamicChecklistRuntimeValue,
  scrutinyApiData: any,
  options?: ScrutinyPrefillOptions
): void {
  const scrutinyDeed = scrutinyApiData?.deed;
  const hasSurveyDocuments =
    Array.isArray(scrutinyApiData?.surveyDocuments) && scrutinyApiData.surveyDocuments.length > 0;
  const hasDocumentsByNatureDoc =
    scrutinyApiData?.documentsByNatureDoc &&
    typeof scrutinyApiData.documentsByNatureDoc === "object" &&
    Object.keys(scrutinyApiData.documentsByNatureDoc).length > 0;
  const missingLodRows = Array.isArray(scrutinyApiData?.missingLOD?.sheets)
    ? (scrutinyApiData.missingLOD.sheets as any[]).map((r) => normalizeMissingLodRow(r))
    : [];
  const propertyDetails =
    scrutinyApiData?.propertyDetails && typeof scrutinyApiData.propertyDetails === "object"
      ? normalizeScrutinyDeedRow(scrutinyApiData.propertyDetails)
      : null;
  const addonScalarRow = normalizeAddonScalarRow(scrutinyApiData?.addonFeatures);
  const preDisbursementRows = Array.isArray(scrutinyApiData?.addonFeatures?.preDisbursementDocuments)
    ? (scrutinyApiData.addonFeatures.preDisbursementDocuments as any[]).map((r) =>
        normalizeDisbursementRow(r)
      )
    : [];
  const postDisbursementRows = Array.isArray(scrutinyApiData?.addonFeatures?.postDisbursementDocuments)
    ? (scrutinyApiData.addonFeatures.postDisbursementDocuments as any[]).map((r) =>
        normalizeDisbursementRow(r)
      )
    : [];
  const surveyVerificationRow = normalizeSurveyVerificationRow(scrutinyApiData?.surveyNoVerification);
  const hasSurveyVerificationRow = Object.keys(surveyVerificationRow).length > 0;
  const generalDetailsByDocument =
    scrutinyApiData?.generalDetailsByDocument &&
    typeof scrutinyApiData.generalDetailsByDocument === "object"
      ? (scrutinyApiData.generalDetailsByDocument as Record<
          string,
          GeneralDetailsDocumentHierarchy
        >)
      : null;
  const hasGeneralDetailsHierarchy =
    generalDetailsByDocument != null && Object.keys(generalDetailsByDocument).length > 0;
  const hasEcVerificationBlocks = ecBlocksFromScrutiny(scrutinyApiData).length > 0;
  if (
    (!scrutinyDeed || typeof scrutinyDeed !== "object") &&
    !hasSurveyDocuments &&
    !hasDocumentsByNatureDoc &&
    !propertyDetails &&
    !addonScalarRow &&
    !preDisbursementRows.length &&
    !postDisbursementRows.length &&
    !hasSurveyVerificationRow &&
    !hasGeneralDetailsHierarchy &&
    !hasEcVerificationBlocks &&
    !missingLodRows.length
  ) {
    return;
  }

  const { checklistValues, repeatableValues } = runtime;

  sections.forEach((section) => {
    const missingLodMappedFields = section.fields.filter((f) => {
      if (f.deedMasterId != null) return false;
      const tag = normalizeDeedIdForSelect(f.scrutinyLinkedDeedId);
      return !!tag && isMissingLodTag(tag);
    });

    const preDisbursementMappedFields = section.fields.filter((f) => {
      if (f.deedMasterId != null) return false;
      const tag = normalizeDeedIdForSelect(f.scrutinyLinkedDeedId);
      return !!tag && isPreDisbursementTag(tag);
    });

    const postDisbursementMappedFields = section.fields.filter((f) => {
      if (f.deedMasterId != null) return false;
      const tag = normalizeDeedIdForSelect(f.scrutinyLinkedDeedId);
      return !!tag && isPostDisbursementTag(tag);
    });

    if (missingLodMappedFields.length && missingLodRows.length) {
      const key = repeatableEntriesKey(section.id, MISSING_LOD_REPEATABLE_SUFFIX);
      const existingRows = repeatableValues[key] || [];
      const rows: Record<string, string>[] = [];
      for (let i = 0; i < missingLodRows.length; i++) {
        const source = missingLodRows[i];
        const row: Record<string, string> =
          existingRows[i] && typeof existingRows[i] === "object"
            ? { ...existingRows[i] }
            : {};
        row[MISSING_LOD_META_FILE_NO_KEY] = String(source.fileNo ?? "").trim();
        row[MISSING_LOD_META_FILE_NAME_KEY] = String(source.fileName ?? "").trim();
        missingLodMappedFields.forEach((f) => {
          const existing = String(row[f.id] ?? "").trim();
          if (existing) return;
          const tag = normalizeDeedIdForSelect(f.scrutinyLinkedDeedId);
          if (!tag) return;
          const next = String(source[tag] ?? "").trim();
          if (next) row[f.id] = next;
        });
        rows.push(row);
      }
      repeatableValues[key] = rows;
    }

    applyDisbursementRepeatablePrefill(
      section,
      preDisbursementMappedFields,
      preDisbursementRows,
      repeatableValues,
      PRE_DISBURSEMENT_REPEATABLE_SUFFIX
    );

    applyDisbursementRepeatablePrefill(
      section,
      postDisbursementMappedFields,
      postDisbursementRows,
      repeatableValues,
      POST_DISBURSEMENT_REPEATABLE_SUFFIX
    );

    section.fields.forEach((f) => {
      if (f.deedMasterId != null) return;
      const tag = normalizeDeedIdForSelect(f.scrutinyLinkedDeedId);
      if (!tag) return;

      // Missing LOD section mapping (`missingLOD.sheets[]`): fileNo, totalSheets,
      // availableSheets, missingSheets. Uses first row that has the requested tag value.
      if (missingLodRows.length && !missingLodMappedFields.length) {
        for (let i = 0; i < missingLodRows.length; i++) {
          const row = missingLodRows[i];
          const val = String(row[tag] ?? "").trim();
          if (String(val || "").trim() !== "") {
            prefillFieldIfEmptyStrict(checklistValues, f, row);
            break;
          }
        }
      }

      if (propertyDetails) {
        prefillFieldIfEmpty(checklistValues, f, propertyDetails);
      }
      if (addonScalarRow) {
        prefillFieldIfEmpty(checklistValues, f, addonScalarRow);
      }
      // Standalone tag fields: use first matching deed row of any type (rare).
      for (const deedId of Object.keys(DEED_MASTER_ID_TO_API_KEY).map(Number)) {
        const rows = scrutinyRowsForDeed(scrutinyApiData, deedId, options?.deedMasterList);
        if (!rows.length) continue;
        prefillFieldIfEmpty(checklistValues, f, normalizeScrutinyDeedRow(rows[0]));
        break;
      }
    });

    deedLinkedGroups(section).forEach((g) => {
      applyGroupScrutinyPrefill(
        section,
        g,
        repeatableValues,
        checklistValues,
        scrutinyApiData,
        options?.deedMasterList,
        missingLodRows
      );
    });

    sectionGateFields(section).forEach((gate) => {
      const group = deedLinkedGroups(section).find(
        (g) => String(g.deedMasterId) === String(gate.deedMasterId)
      );
      if (!group) return;
      const rows = scrutinyRowsForDeed(scrutinyApiData, group.deedMasterId, options?.deedMasterList);
      if (!rows.length) {
        checklistValues[gate.id] = "No";
        return;
      }
      if (options?.autoOpenDeedGates && (checklistValues[gate.id] ?? "").trim() !== "Yes") {
        checklistValues[gate.id] = "Yes";
      }
    });
  });

  applySurveyVerificationPrefill(
    sections,
    checklistValues,
    repeatableValues,
    scrutinyApiData,
    {
      activeDocumentFdId: options?.activeDocumentFdId,
      propertyDetails,
      generalDetailsByDocument,
      forceHierarchyFields: false,
    }
  );

  applyEcVerificationPrefill(sections, repeatableValues, scrutinyApiData);

  applyScrutinyFollowUpPrefill(
    sections,
    checklistValues,
    repeatableValues,
    scrutinyApiData,
    options?.deedMasterList,
    missingLodRows
  );
}

/** Ignore seeded checkbox defaults when detecting a persisted snapshot. */
function isMeaningfulSavedChecklistScalar(value: unknown): boolean {
  const text = value != null ? String(value).trim() : "";
  if (!text) return false;
  if (text === "false") return false;
  return true;
}

/** True when saved dynamic snapshot has any non-empty repeatable cell value. */
export function hasSavedRepeatableFieldAnswers(
  initial?: Partial<DynamicChecklistRuntimeValue> | null
): boolean {
  if (!initial?.repeatableValues) return false;
  const rv = initial.repeatableValues;
  return Object.keys(rv).some((k) =>
    (rv[k] || []).some((row) =>
      Object.values(row || {}).some((v) => isMeaningfulSavedChecklistScalar(v))
    )
  );
}

export function hasSavedDynamicAnswers(initial?: Partial<DynamicChecklistRuntimeValue> | null): boolean {
  if (!initial) return false;
  const cv = initial.checklistValues;
  if (cv && Object.keys(cv).some((k) => isMeaningfulSavedChecklistScalar(cv[k]))) {
    return true;
  }
  return hasSavedRepeatableFieldAnswers(initial);
}

export function hasPersistedDynamicSnapshot(
  initial?: Partial<DynamicChecklistRuntimeValue> | null,
  persisted?: boolean
): boolean {
  if (persisted === true) return true;
  return hasSavedDynamicAnswers(initial);
}

/** Compact checklist JSON for DB — keeps `dynamic` small so it is not truncated by huge legacy deed blobs. */
export function buildDynamicChecklistPersistPayload(
  dynamic: DynamicChecklistRuntimeValue,
  checklistSubmitStatus: string,
  remarks?: string
): Record<string, unknown> {
  return {
    remarks: remarks ?? "",
    checklistSubmitStatus,
    dynamic: {
      checklistValues: { ...dynamic.checklistValues },
      repeatableValues: Object.keys(dynamic.repeatableValues || {}).reduce<
        Record<string, Record<string, string>[]>
      >((acc, k) => {
        const rows = dynamic.repeatableValues[k];
        acc[k] = Array.isArray(rows)
          ? rows.map((r) =>
              r && typeof r === "object"
                ? Object.keys(r).reduce<Record<string, string>>((row, kk) => {
                    row[kk] = r[kk] == null ? "" : String(r[kk]);
                    return row;
                  }, {})
                : {}
            )
          : [];
        return acc;
      }, {}),
      fieldVerified: { ...(dynamic.fieldVerified || {}) },
    },
  };
}
